import React, { useRef, useState } from "react";

// Flag images are loaded via URLs from VP-Flags or Kowo-Flags folders

function getAutoFlag(marker) {
  // Ako marker.flag postoji, koristi ga
  if (marker.flag) {
    // Osiguraj da path ima leading slash
    if (marker.flag.startsWith("/")) return marker.flag;
    return marker.flag;
  }
  // Flags are now assigned through BulkLootspotEditor
  return "";
}

function Marker({
  marker,
  scale,
  onChange,
  onRemove,
  onEdit,
  mapContainerRef,
  showDev,
  showCoords,
  onAddTempMarker,
  isTemp,
}) {
  const markerRef = useRef(null);
  const textRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [showTooltip, setShowTooltip] = useState(false);

  // Drag logic
  const handleMouseDown = (e) => {
    if (!showDev) return; // samo u dev modu može pomicati
    e.stopPropagation();
    setDragging(true);
    // Izračunaj offset unutar markera
    if (markerRef.current) {
      const rect = markerRef.current.getBoundingClientRect();
      setOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    } else {
      setOffset({ x: 0, y: 0 });
    }
    document.body.style.cursor = "grabbing";
  };

  React.useEffect(() => {
    if (!dragging) return;
    // Fallback: try to find the map container if not provided
    let mapRef = mapContainerRef && mapContainerRef.current ? mapContainerRef.current : null;
    if (!mapRef && markerRef.current) {
      // Try to find closest parent with class 'map-anim'
      let el = markerRef.current.parentElement;
      while (el && !el.classList.contains("map-anim")) el = el.parentElement;
      if (el) mapRef = el;
    }
    const handleMove = (e) => {
      if (!mapRef) return;
      const mapRect = mapRef.getBoundingClientRect();
      // Pozicija miša unutar mape
      let relX =
        (e.clientX - mapRect.left - offset.x + (markerRef.current?.offsetWidth || 0) / 2) /
        mapRect.width;
      let relY =
        (e.clientY - mapRect.top - offset.y + (markerRef.current?.offsetHeight || 0)) /
        mapRect.height;
      relX = Math.max(0, Math.min(1, relX));
      relY = Math.max(0, Math.min(1, relY));
      onChange({ ...marker, x: relX, y: relY });
    };
    const handleUp = () => {
      setDragging(false);
      document.body.style.cursor = "";
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [dragging, offset, marker, onChange, mapContainerRef, showDev /* bez user-a */]);

  // Remove marker
  const handleRemove = (e) => {
    // Contest marker se može brisati uvijek, ostali samo u dev modu
    if (!(marker.id && marker.id.endsWith("_contest1")) && !showDev) return;
    e.stopPropagation();
    if (onRemove) onRemove(marker.id);
  };

  const effectiveShowTooltip = showTooltip;

  const autoFlag = getAutoFlag(marker);

  return (
    <div
      ref={markerRef}
      className={`group absolute z-20 marker-root${isTemp ? "marker-temp" : ""}`}
      data-team={marker.team || ""}
      style={{
        left: `${((marker.x ?? 0.5) + (marker.x_offset ?? 0)) * 100}%`,
        top: `${marker.y * 100}%`,
        transform: "translate(-50%, -100%)",
        cursor: showDev ? (dragging ? "grabbing" : "pointer") : "pointer",
        userSelect: "none",
        transition: dragging ? "none" : "box-shadow 0.18s",
        zIndex: 20,
      }}
      onMouseDown={handleMouseDown}
      onClick={(e) => {
        // Spriječi otvaranje edit modala kod draganja
        if (dragging) return;
        e.stopPropagation();
        if (onEdit) onEdit(marker);
      }}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {/* Show coordinates above marker for devs if enabled */}
      {showDev && showCoords && (
        <div
          style={{
            position: "absolute",
            top: "-1.7rem",
            left: "50%",
            transform: "translateX(-50%)",
            background: "#23244d",
            color: "#e0aaff",
            fontSize: "0.85rem",
            padding: "2px 10px",
            borderRadius: 6,
            fontWeight: 700,
            zIndex: 30,
            pointerEvents: "none",
            boxShadow: "0 2px 8px #e0aaff55",
            whiteSpace: "nowrap",
          }}
        >
          ({(marker.x * 100).toFixed(4)}, {(marker.y * 100).toFixed(4)})
        </div>
      )}
      {/* Always visible tier label above marker */}
      {marker.tier && (
        <div
          className="marker-tier-label border-2 border-ewave-border bg-ewave-dark text-primary"
          style={{
            position: "absolute",
            top: "-1.3rem",
            left: "50%",
            transform: "translateX(-50%)",
            fontWeight: 800,
            fontSize: `${0.92 * scale}rem`,
            padding: `${2 * scale}px ${10 * scale}px`,
            borderRadius: 7 * scale,
            zIndex: 100,
            pointerEvents: "none",
            whiteSpace: "nowrap",
            opacity: 1,
            marginBottom: "2px",
          }}
        >
          {marker.tier}
        </div>
      )}
      {/* Marker shape or logo */}
      {marker.logo ? (
        <div style={{ position: "relative", display: "inline-block" }}>
          <img
            src={marker.logo}
            alt={marker.team || ""}
            style={{
              width: 80 * (marker.size || 1) * scale,
              height: 80 * (marker.size || 1) * scale,
              objectFit: "cover",
              display: "block",
              border: "2px solid var(--color-primary)",
              background: "rgba(35,36,77,0.68)",
              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
              borderRadius: "50%",
              padding: 0,
              margin: 0,
              maxWidth: "100%",
              maxHeight: "100%",
              transition: "transform 0.2s ease",
              position: "relative",
              zIndex: 2,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.12)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
            }}
          />
          {/* Ako postoji flag, prikaži ga kao full overlay preko loga */}
          {autoFlag && (
            <>
              <img
                src={autoFlag}
                alt="flag"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: 80 * (marker.size || 1) * scale,
                  height: 80 * (marker.size || 1) * scale,
                  borderRadius: "50%",
                  objectFit: "cover",
                  zIndex: 5,
                  pointerEvents: "none",
                }}
              />
              {/* Team name overlayed over flag if both logo and flag exist */}
              {marker.team && (
                <span
                  ref={textRef}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                    fontSize:
                      marker.team.length > 32
                        ? 0.44 * scale + "rem"
                        : marker.team.length > 26
                          ? 0.52 * scale + "rem"
                          : marker.team.length > 22
                            ? 0.6 * scale + "rem"
                            : marker.team.length > 18
                              ? 0.68 * scale + "rem"
                              : marker.team.length > 14
                                ? 0.78 * scale + "rem"
                                : marker.team.length > 11
                                  ? 0.88 * scale + "rem"
                                  : marker.team.length > 8
                                    ? 0.99 * scale + "rem"
                                    : 1.09 * scale + "rem",
                    color: "#fff",
                    whiteSpace: "nowrap",
                    maxWidth: "88%",
                    padding: "0 6%",
                    overflow: "visible",
                    userSelect: "none",
                    pointerEvents: "none",
                    textAlign: "center",
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    transform: "translate(-50%, -50%) scaleY(1.5)",
                    zIndex: 10,
                    margin: 0,
                    height: "auto",
                    lineHeight: 1,
                    textShadow:
                      "0 0 2px #000, 0 1px 8px #0008, 2px 2px 8px rgba(0,0,0,0.95), 0 0 6px #000, 0 0 12px #000, 0 0 1px #000",
                  }}
                >
                  {marker.team}
                </span>
              )}
            </>
          )}
          {marker.slot && (
            <div
              style={{
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: "translate(-50%, 35%)",
                background: "rgba(21,17,34,0.85)",
                color: "#ff3461",
                fontWeight: 800,
                fontSize: `${0.78 * scale}rem`,
                padding: `${2 * scale}px ${8 * scale}px`,
                borderRadius: 8 * scale,
                zIndex: 7,
                whiteSpace: "nowrap",
                pointerEvents: "none",
                border: "1px solid rgba(255,52,97,0.6)",
                boxShadow: "0 2px 6px rgba(0,0,0,0.6)",
              }}
            >
              Slot {String(marker.slot)}
            </div>
          )}
        </div>
      ) : (
        <div
          className="relative flex flex-col items-center bg-ewave-dark/60"
          style={{
            width: 80 * (marker.size || 1) * scale,
            height: 80 * (marker.size || 1) * scale,
            objectFit: "cover",
            display: "block",
            border: "2px solid var(--color-primary)",
            background: "rgba(35,36,77,0.68)",
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            borderRadius: "50%",
            padding: 0,
            margin: 0,
            maxWidth: "100%",
            maxHeight: "100%",
            transition: "transform 0.2s ease",
            position: "relative",
            zIndex: 2,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.12)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
          }}
        >
          {/* Flag overlay for markers without logo */}
          {autoFlag && (
            <div
              style={{
                width: 80 * (marker.size || 1) * scale,
                height: 80 * (marker.size || 1) * scale,
                objectFit: "cover",
                display: "block",
                background: "rgba(35,36,77,0.68)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                borderRadius: "50%",
                padding: 0,
                margin: 0,
                maxWidth: "100%",
                maxHeight: "100%",
                transition: "transform 0.2s ease",
                position: "relative",
                zIndex: 3,
              }}
            >
              <img
                src={autoFlag}
                alt="flag"
                style={{
                  width: 80 * (marker.size || 1) * scale,
                  height: 80 * (marker.size || 1) * scale,
                  objectFit: "cover",
                  display: "block",
                  background: "rgba(35,36,77,0.68)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                  borderRadius: "50%",
                  padding: 0,
                  margin: 0,
                  maxWidth: "100%",
                  maxHeight: "100%",
                  transition: "transform 0.2s ease",
                  position: "relative",
                  zIndex: 3,
                }}
              />
            </div>
          )}
      {marker.slot && (
            <div
              style={{
                position: "absolute",
                left: "50%",
        top: "50%",
        transform: "translate(-50%, 35%)",
                background: "rgba(21,17,34,0.85)",
                color: "#ff3461",
                fontWeight: 800,
                fontSize: `${0.78 * scale}rem`,
                padding: `${2 * scale}px ${8 * scale}px`,
                borderRadius: 8 * scale,
                zIndex: 7,
                whiteSpace: "nowrap",
                pointerEvents: "none",
                border: "1px solid rgba(255,52,97,0.6)",
                boxShadow: "0 2px 6px rgba(0,0,0,0.6)",
              }}
            >
              Slot {String(marker.slot)}
            </div>
          )}
          <span
            ref={textRef}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 800,
              fontSize:
                marker.team && marker.team.length > 32
                  ? 0.44 * scale + "rem"
                  : marker.team && marker.team.length > 26
                    ? 0.52 * scale + "rem"
                    : marker.team && marker.team.length > 22
                      ? 0.6 * scale + "rem"
                      : marker.team && marker.team.length > 18
                        ? 0.68 * scale + "rem"
                        : marker.team && marker.team.length > 14
                          ? 0.78 * scale + "rem"
                          : marker.team && marker.team.length > 11
                            ? 0.88 * scale + "rem"
                            : marker.team && marker.team.length > 8
                              ? 0.99 * scale + "rem"
                              : 1.09 * scale + "rem",
              color: "#fff",
              whiteSpace: "nowrap",
              maxWidth: "88%",
              padding: "0 6%",
              overflow: "visible",
              userSelect: "none",
              pointerEvents: "none",
              textAlign: "center",
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%) scaleY(1.5)",
              zIndex: 10, // povećan z-index za ime tima
              margin: 0,
              height: "auto",
              lineHeight: 1,
              textShadow:
                "0 0 2px #000, 0 1px 8px #0008, 2px 2px 8px rgba(0,0,0,0.95), 0 0 6px #000, 0 0 12px #000, 0 0 1px #000", // još jači shadow
            }}
          >
            {marker.team}
          </span>
        </div>
      )}
      {/* Tooltip for team/tier info */}
      {effectiveShowTooltip && (marker.team || marker.tier) && (
        <div
          className="pointer-events-none absolute bottom-16 left-2/4 z-[9999] whitespace-nowrap border border-ewave-border bg-ewave-dark text-center text-primary"
          style={{
            transform: "translateX(-50%)",
            padding: `${6 * scale}px ${16 * scale}px`,
            borderRadius: 8 * scale,
            fontWeight: 700,
            fontSize: 1.05 * scale + "rem",
            maxWidth: 260 * scale,
          }}
        >
          {marker.team || ""}
          {marker.team && marker.tier ? " | " : ""}
          {marker.tier || ""}
        </div>
      )}
      {/* Marker ID for devs */}
      {showDev && (
        <div
          className="pointer-events-none absolute bottom-1 left-1 select-none rounded bg-black bg-opacity-60 px-2 py-0.5 text-xs text-white"
          style={{ zIndex: 20 }}
        >
          {marker.markerName && marker.markerName.trim()
            ? marker.markerName
            : marker.id || "NO NAME"}
        </div>
      )}
      {/* Remove button for contest marker */}
      {(isTemp || (marker.id && marker.id.endsWith("_contest1"))) && (
        <button
          className="screenshot-hide absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-red-600 text-xs font-bold text-white opacity-100 shadow transition-opacity"
          style={{ zIndex: 10 }}
          onClick={handleRemove}
          tabIndex={-1}
        >
          ×
        </button>
      )}
      {showDev && (
        <button
          className="screenshot-hide absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-red-600 text-xs font-bold text-white opacity-0 shadow transition-opacity group-hover:opacity-100"
          style={{ zIndex: 10 }}
          onClick={handleRemove}
          tabIndex={-1}
        >
          ×
        </button>
      )}
    </div>
  );
}

export default React.memo(Marker);
